<template>
  <app-modal :model-value="isDialogOpen"
             :ok-text="$t('common.move')"
             :ok-loading="isPending"
             :ok-disabled="!selectedFolder.length"
             size="md"
             @ok="isRoom ? prepareMoveRoom() : prepareMoveProjectsFolder()"
             @cancel="close"
  >
    <template #title>
      <div class="font-weight-bold">
        <template v-if="isRoom">
          {{ $t('projects.dialogs.ProjectsMoveRoomDialog.moveRoom', { name: roomToMove.name }) }}
        </template>
        <template v-else>
          {{ $t('projects.dialogs.ProjectsFoldersMoveFolderDialog.moveFolder', { name: projectsFolderToMove.name }) }}
        </template>
      </div>
    </template>
    <template #body>
      <div v-if="getProjectsFoldersTreePending && !projectsFoldersTree.length" class="text-center">
        <AppLoader small/>
      </div>
      <template v-else>
        <div class="mb-4">{{ $t('projects.dialogs.ProjectsMoveRoomDialog.selectDestinationFolder') }}</div>
        <AppClosdFildersTree
          v-model:value="selectedFolder"
          :root-folder="fullTree"
          foldersOnly
          :multiple="false"
        />
      </template>
    </template>
  </app-modal>
</template>

<script>
  import { cloneDeep } from 'lodash-es'
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

  import AppClosdFildersTree from '@/common/app-closd-filders-tree/AppClosdFildersTree.vue'
  import AppLoader from '@/common/AppLoader.vue'
  import { FOLDER_MAX_DEPTH } from '@/common/constants'
  import { toast } from '@/common/utils/toast'
  import projectsFoldersMixin from '@/mixins/projectsFoldersMixin'
  import {
    MOVE_ROOM_INTO_FOLDER,
  } from '@/store/modules/projects-folders/action_types'
  import {
    SET_ROOM_TO_MOVE,
    SET_MOVE_ROOM_DIALOG_IS_OPEN,
  } from '@/store/modules/projects-folders/mutation_types'
  import { MOVE_PROJECTS_FOLDER } from '@/store/modules/projects-folders-move/action_types'
  import {
    SET_PROJECTS_FOLDER_MOVE_DIALOG_IS_OPEN,
    SET_PROJECTS_FOLDER_TO_MOVE,
  } from '@/store/modules/projects-folders-move/mutation_types'

  export default {
    name: 'ProjectsMoveRoomDialog',
    components: { AppLoader, AppClosdFildersTree },
    mixins: [projectsFoldersMixin],
    props: {
      // Is Room or Folder
      isRoom: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        selectedFolder: [],
        FOLDER_MAX_DEPTH,
      }
    },
    computed: {
      ...mapGetters('rooms', ['rooms']),
      ...mapState('projectsFolders', ['projectsFoldersTree', 'currentProjectsFolder', 'getProjectsFoldersTreePending', 'roomToMove', 'moveRoomDialogIsOpen', 'moveRoomPending']),
      ...mapState('projectsFoldersMove', ['projectsFolderMovePending', 'projectsFolderMoveDialogIsOpen', 'projectsFolderToMove']),
      isDialogOpen () {
        return this.isRoom ? this.moveRoomDialogIsOpen : this.projectsFolderMoveDialogIsOpen
      },
      isPending () {
        return this.isRoom ? this.moveRoomPending : this.projectsFolderMovePending
      },
      fullTree () {
        return {
          id: 0,
          icon: 'home_storage',
          type: 'folder',
          unSelectable: this.isRootFolderUnSelectable,
          basename: this.$t('projects.ProjectsHeader.myProjects'),
          children: this.projectsFoldersTreeMapping,
        }
      },
      isRootFolderUnSelectable () {
        return this.projectsFolderToMove.parentId === null || !this.currentProjectsFolder.id
      },
      projectsFoldersTreeMapping () {
        const datas = cloneDeep(this.projectsFoldersTree)

        datas.forEach(folder => {
          // Disable selection of the folder to be moved
          folder.locked = (folder.id === this.projectsFolderToMove.id)
          folder.type = 'folder'
          // Makes folder unselectable, but can select its children
          folder.unSelectable = (folder.id === (this.projectsFolderToMove.parentId || this.currentProjectsFolder.id))

          if (folder.children.length !== 0) {
            folder.childrenCount = folder.children.length
            folder.children.map(this.setChildrenLockedProp)
          } else {
            delete folder.children // To avoid displaying the tooggle icon in front of the folder name
          }
        })

        return datas
      },
    },
    methods: {
      ...mapMutations('projectsFolders', [SET_ROOM_TO_MOVE, SET_MOVE_ROOM_DIALOG_IS_OPEN]),
      ...mapActions('projectsFolders', [MOVE_ROOM_INTO_FOLDER]),
      ...mapActions('projectsFoldersMove', [MOVE_PROJECTS_FOLDER]),
      ...mapMutations('projectsFoldersMove', [SET_PROJECTS_FOLDER_MOVE_DIALOG_IS_OPEN, SET_PROJECTS_FOLDER_TO_MOVE]),
      setChildrenLockedProp (item) {
        // Only locked when is a folder, because projects can be moved to any folder
        if (this.projectsFolderToMove.id) {
          if (item.id === this.projectsFolderToMove.id ||
              item.depth === FOLDER_MAX_DEPTH) { // siblings that are leaves cannot be moved one inside the other
            item.locked = true
          }
        }
        if (item.children?.length) {
          item.children = item.children.map(this.setChildrenLockedProp)
        } else {
          delete item.children
        }
        item.type = 'file'
        return item
      },
      async prepareMoveRoom () {
        if (!this.roomToMove.id) return
        try {
          const { name, id } = this.roomToMove
          await this.MOVE_ROOM_INTO_FOLDER({
            toFolderId: this.selectedFolder[0].id,
            roomId: id,
          })

          this.updateCurrentAndDestinationFolders(true)

          this.SET_MOVE_ROOM_DIALOG_IS_OPEN(false)

          toast.success(this.$t('projects.dialogs.ProjectsMoveRoomDialog.moveRoomSuccess', {
            projectName: name,
            folderName: this.selectedFolder[0].name,
          }))
        } catch (e) {
          toast.error(this.$t('projects.dialogs.ProjectsMoveRoomDialog.moveRoomError'))
        }
      },
      async prepareMoveProjectsFolder () {
        if (!this.projectsFolderToMove.id) return

        const { name } = this.projectsFolderToMove
        try {
          await this.MOVE_PROJECTS_FOLDER({
            projectsFolderToMoveId: this.projectsFolderToMove.id,
            toFolderId: this.selectedFolder[0].id,
          })

          this.updateCurrentAndDestinationFolders()

          this.SET_PROJECTS_FOLDER_MOVE_DIALOG_IS_OPEN(false)

          toast.success(this.$t('projects.dialogs.ProjectsFoldersMoveFolderDialog.moveFolderSuccess', {
            folderName: name,
            destinationFolderName: this.selectedFolder[0].name,
          }))
        } catch (e) {
          if (e.response?.data?.errorCode === 'TOO_MANY_PARENT') {
            toast.error(this.$t('projects.dialogs.ProjectsFoldersMoveFolderDialog.moveFolderErrorTooManyParent', {
              folderName: name,
            }))
          } else {
            toast.error(this.$t('projects.dialogs.ProjectsFoldersMoveFolderDialog.moveFolderError'))
          }
        }
      },
      close () {
        if (this.isRoom) {
          this.SET_MOVE_ROOM_DIALOG_IS_OPEN(false)
          this.SET_ROOM_TO_MOVE({})
        } else {
          this.SET_PROJECTS_FOLDER_MOVE_DIALOG_IS_OPEN(false)
          this.SET_PROJECTS_FOLDER_TO_MOVE({})
        }
      },
    },
  }
</script>
